import { FC } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { TextField } from 'components/Forms/TextField';
import { FormApi } from 'final-form';
import { required } from 'helpers/utils';
import { PositionCategoryOptions, roleTypeOptions, subscriptionLevelOptions } from './constants';
import { SelectField } from 'components/Forms/SelectField';
import { PhoneAdapter } from 'components/Forms/PhoneField';
import { SearchWithOptions } from 'components/Forms/SearchWithOptions';
import { getRecipients } from 'store/recipients/actions';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { getRecipientsData } from 'selectors/recipients';
import { RadioField } from 'components/Forms/RadioField';
import { getPartnersData } from 'selectors/partners';
import { getPartners } from 'store/partners/actions';
import { getActiveRole } from 'selectors/admins';
import { RecipientsRequestType } from 'store/recipients/types';

interface Props {
  index: number;
  itemVisible: number;
  formState: FormApi<Record<string, any>>;
  isLoading: boolean;
  isEditMode: boolean;
}

export const AdminAccordion: FC<Props> = ({
  index,
  itemVisible,
  formState,
  isLoading,
  isEditMode,
}) => {
  const dispatch = useAppDispatch();
  const recipientsData = useAppSelector(getRecipientsData);
  const partnersData = useAppSelector(getPartnersData);
  const activeRole = useAppSelector(getActiveRole);

  const removeItemOption = (option: any) => {
    if (formState.getState().values.role_type === 'partner_admin') {
      formState.change(
        'partnersSelected',
        formState.getState().values.partnersSelected.filter(item => item.value !== option.value)
      );
    } else {
      formState.change(
        'recipientsSelected',
        formState.getState().values.recipientsSelected.filter(item => item.value !== option.value)
      );
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ height: 0 }}
        animate={itemVisible === index ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
        className="motion-card-container"
      >
        {index === 0 && (
          <div className="card">
            <div className="card-body card-body-page-detail">
              <div className="row">
                <div className="col-md-4 padding0">
                  <TextField
                    label="First Name *"
                    name="first_name"
                    type="text"
                    validate={isEditMode ? null : required}
                    disabled={isEditMode}
                  />
                </div>
                <div className="col-md-2" />
                <div className="col-md-4 padding0">
                  <TextField
                    label="Last Name *"
                    name="last_name"
                    type="text"
                    validate={isEditMode ? null : required}
                    disabled={isEditMode}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-4 padding0">
                  <TextField
                    label="Email *"
                    name="email"
                    type="text"
                    validate={isEditMode ? null : required}
                    disabled={isEditMode}
                  />
                </div>
                <div className="col-md-2" />
                <div className="col-md-4 padding0">
                  <TextField
                    label="Phone Number"
                    name="phone_number"
                    component={PhoneAdapter}
                    disabled={isEditMode}
                  />
                </div>
              </div>
              {!isEditMode && (
                <div className="row">
                  <div className="col-md-4 padding0">
                    <div className="form-input">
                      <label htmlFor="position_category">Select Position Category *</label>
                      <SelectField
                        name="position_category"
                        options={PositionCategoryOptions}
                        validate={required}
                      />
                    </div>
                  </div>
                  <div className="col-md-2" />
                  {formState.getState().values.position_category === 'OTHER' && (
                    <div className="col-md-4 padding0">
                      <TextField
                        label="Other Position Category *"
                        name="other_position_category"
                        type="text"
                        validate={required}
                        disabled={isEditMode}
                      />
                    </div>
                  )}
                </div>
              )}
              <div className="row">
                <div className="col-md-4 padding0">
                  <RadioField
                    name="role_type"
                    label={isEditMode ? 'Add Role Type Resources *' : 'Role Type *'}
                    options={roleTypeOptions}
                  />
                </div>
                <div className="col-md-2" />
                {formState.getState().values.role_type === 'recipient_admin' && (
                  <div className="col-md-6 padding0">
                    <div className="checkbox-container">
                      <div className="form-check form-check-inline">
                        <label htmlFor="checkbox-primary_contact" className="form-check-label">
                          <input
                            id="checkbox-primary_contact"
                            type="checkbox"
                            value="primary_contact"
                            checked={formState.getState().values.primary_contact}
                            onChange={() =>
                              formState.change(
                                'primary_contact',
                                !formState.getState().values.primary_contact
                              )
                            }
                          />
                          <span className="form-check-sign"></span>
                        </label>
                      </div>
                      <span>Check if contact is the Primary Contact</span>
                    </div>
                    <span className="utility-text color-black">
                      (The Primary Contact is the only person who can complete Stripe Onboarding)
                    </span>
                  </div>
                )}
              </div>
              <div className="row">
                <div className="col-md-4 padding0">
                  {formState.getState().values.role_type === 'recipient_admin' ? (
                    <SearchWithOptions
                      type="recipient"
                      label="Recipients *"
                      data={recipientsData}
                      optionsSelected={formState.getState().values.recipientsSelected}
                      setOptionsSelected={recipientsSelected =>
                        formState.change('recipientsSelected', recipientsSelected)
                      }
                      getOptionsFromAPI={search_str =>
                        dispatch(
                          getRecipients({
                            search_str,
                            payment_processor: 'any',
                            request_type: RecipientsRequestType.admin,
                          })
                        )
                      }
                      options={recipientsData?.map(rec => ({
                        value: rec.recipient_id,
                        label: rec.recipient_name || '',
                      }))}
                      removeItemOption={removeItemOption}
                    />
                  ) : (
                    formState.getState().values.role_type === 'partner_admin' && (
                      <RadioField
                        name="subscription_level"
                        label="Subscription Level *"
                        options={subscriptionLevelOptions}
                      />
                    )
                  )}
                </div>
                <div className="col-md-2 padding0" />
                <div className="col-md-4 padding0">
                  {formState.getState().values.role_type === 'partner_admin' && (
                    <SearchWithOptions
                      type="partner"
                      label="Partners *"
                      data={partnersData}
                      optionsSelected={formState.getState().values.partnersSelected}
                      setOptionsSelected={partnersSelected =>
                        formState.change('partnersSelected', partnersSelected)
                      }
                      getOptionsFromAPI={search_str =>
                        dispatch(getPartners({ search_str, partner_ids: [] }, activeRole))
                      }
                      options={
                        partnersData?.map(partner => ({
                          value: String(partner.client_id),
                          label: partner.client_name || '',
                        })) || []
                      }
                      removeItemOption={removeItemOption}
                    />
                  )}
                </div>
              </div>
              <button
                className="btn-inkBlue row submit-button btn btn-secondary"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? 'Submitting...' : isEditMode ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
